import { useCallback, useMemo } from "react";
import {
  NetworkStatus,
  type DocumentNode,
  type OperationVariables,
  type TypedDocumentNode,
  type Unmasked,
} from "@apollo/client";

import { useMutation, useQuery } from "@apollo/client/react";

import type { ShareDialogSummary } from "#app/components/sharing/share-dialog";
import type {
  ItemAccessSummary,
  ObjectAccessLevel,
  WorkflowAccessSummary,
} from "#app/lib/data";

// Generic engine behind useItemSharing / useWorkflowSharing. The two
// GraphQL APIs are deliberate mirrors — the same paginated access
// summary query plus the same seven share mutations, differing only in
// the id argument name and the summary root field — so one hook owns
// the pagination, fetchMore merging, refetch wiring and dialog summary;
// the per-object hooks just supply documents and accessors.

export type RawAccessSummary = ItemAccessSummary | WorkflowAccessSummary;

// The two mirror queries take identical variables apart from the id key,
// so this is the concrete shape both documents are consumed through.
type SummaryVariables = {
  issueId?: string;
  workflowId?: string;
  skip: number;
  limit: number;
  teamSkip: number;
  teamLimit: number;
  search: string | null;
};

const PAGE_SIZE = 50;

type ObjectSharingConfig<
  TData,
  TVars extends OperationVariables,
  TSummary extends RawAccessSummary,
> = {
  id: string | null;
  // The per-object hooks forward their own optional `search` straight through,
  // so an absent key and an explicit `undefined` are the same empty search.
  search?: string | undefined;
  // Name of the id variable shared by the summary query and every
  // mutation in `mutations`.
  idKey: "issueId" | "workflowId";
  summaryDocument: TypedDocumentNode<TData, TVars>;
  mutations: {
    share: DocumentNode;
    shareTeam: DocumentNode;
    removeAllMemberShares: DocumentNode;
    removeAllTeamShares: DocumentNode;
    setAllMemberShares: DocumentNode;
    setAllTeamShares: DocumentNode;
    setVisibility: DocumentNode;
  };
  // Pull the summary root field off the query payload. Module-scope in
  // the per-object hook so callback identities stay stable.
  selectSummary: (data: TData | undefined) => TSummary | null;
  // Rebuild a query payload with one list appended after a fetchMore;
  // `list` is which page was fetched (the other list keeps `prev`'s
  // slice). Lives in the per-object hook so it stays fully typed
  // against that query's shape.
  mergePage: (
    prev: Unmasked<TData>,
    next: Unmasked<TData>,
    list: "members" | "teams",
  ) => Unmasked<TData>;
  // Optional post-visibility-flip hook (workflow sharing re-dispatches
  // AppData so the sidebar's lock chip updates).
  onVisibilityChange?: () => void;
};

export function useObjectSharing<
  TData,
  TVars extends OperationVariables,
  TSummary extends RawAccessSummary,
>({
  id,
  search,
  idKey,
  summaryDocument,
  mutations,
  selectSummary,
  mergePage,
  onVisibilityChange,
}: ObjectSharingConfig<TData, TVars, TSummary>) {
  // The query's id variable name is erased on purpose — `baseVariables`
  // builds it from `idKey`, so the document is consumed through the
  // loosely-typed vars view at this one seam.
  const summaryQuery = summaryDocument as unknown as TypedDocumentNode<
    TData,
    SummaryVariables
  >;
  const baseVariables = useMemo(
    (): SummaryVariables => ({
      [idKey]: id ?? "",
      skip: 0,
      limit: PAGE_SIZE,
      teamSkip: 0,
      teamLimit: PAGE_SIZE,
      search: search ?? null,
    }),
    [idKey, id, search],
  );
  const queryOptions = useMemo(
    () => ({
      variables: baseVariables,
      skip: !id,
      notifyOnNetworkStatusChange: true,
    }),
    [baseVariables, id],
  );
  const { data, previousData, loading, networkStatus, fetchMore } = useQuery(
    summaryQuery,
    queryOptions,
  );
  // Re-entrancy guard for the loadMore callbacks. Keyed on networkStatus
  // — NOT raw `loading`, which is also true while a mutation-triggered
  // refetch is in flight (notifyOnNetworkStatusChange + refetchQueries
  // on every share mutation), and would silently drop a pagination
  // request fired during that window.
  const fetchingMore = networkStatus === NetworkStatus.fetchMore;
  // While a search query is in flight, Apollo briefly returns
  // `data: undefined` until the new result arrives — fall back to the
  // last seen payload so the list stays visible instead of swapping
  // in a spinner. `previousData`'s type unions in `DeepPartial<TData>`
  // for the @defer streaming data state; this app runs no deferred
  // queries, so a previous payload is always complete.
  const displayData = data ?? (previousData as TData | undefined);

  // Mutations refetch the first page of both lists to keep the dialog
  // in sync.
  const refetchQueries = useMemo(
    () => (id ? [{ query: summaryQuery, variables: baseVariables }] : []),
    [id, summaryQuery, baseVariables],
  );
  const [shareMutation] = useMutation(mutations.share, { refetchQueries });
  const [shareTeamMutation] = useMutation(mutations.shareTeam, {
    refetchQueries,
  });
  const [removeAllMembersMutation] = useMutation(
    mutations.removeAllMemberShares,
    { refetchQueries },
  );
  const [removeAllTeamsMutation] = useMutation(mutations.removeAllTeamShares, {
    refetchQueries,
  });
  const [setVisibilityMutation] = useMutation(mutations.setVisibility, {
    refetchQueries,
  });
  const [setAllMembersMutation] = useMutation(mutations.setAllMemberShares, {
    refetchQueries,
  });
  const [setAllTeamsMutation] = useMutation(mutations.setAllTeamShares, {
    refetchQueries,
  });

  const setMemberShare = useCallback(
    (memberId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!id) return;
      void shareMutation({
        variables: {
          input: { [idKey]: id, userId: memberId, accessLevel, enabled },
        },
      });
    },
    [shareMutation, idKey, id],
  );

  const setTeamShare = useCallback(
    (teamId: string, accessLevel: ObjectAccessLevel, enabled: boolean) => {
      if (!id) return;
      void shareTeamMutation({
        variables: {
          input: { [idKey]: id, teamId, accessLevel, enabled },
        },
      });
    },
    [shareTeamMutation, idKey, id],
  );

  const removeAllMembers = useCallback(() => {
    if (!id) return;
    void removeAllMembersMutation({ variables: { [idKey]: id } });
  }, [removeAllMembersMutation, idKey, id]);

  const removeAllTeams = useCallback(() => {
    if (!id) return;
    void removeAllTeamsMutation({ variables: { [idKey]: id } });
  }, [removeAllTeamsMutation, idKey, id]);

  // "Edit all" — bulk-set every (search-matched) member / team row to
  // the chosen level. Sends the active search so the server narrows to
  // the same rows the dialog is showing. Locked rows (Owner / object
  // editors) are skipped server-side.
  const setAllMembers = useCallback(
    (accessLevel: ObjectAccessLevel) => {
      if (!id) return;
      void setAllMembersMutation({
        variables: { [idKey]: id, accessLevel, search: search ?? null },
      });
    },
    [setAllMembersMutation, idKey, id, search],
  );

  const setAllTeams = useCallback(
    (accessLevel: ObjectAccessLevel) => {
      if (!id) return;
      void setAllTeamsMutation({
        variables: { [idKey]: id, accessLevel, search: search ?? null },
      });
    },
    [setAllTeamsMutation, idKey, id, search],
  );

  const togglePrivate = useCallback(() => {
    if (!id) return;
    // Read the CURRENT visibility from displayData (data ?? previousData)
    // — raw `data` goes briefly undefined while a search is in flight, and
    // reading it there would default `current` to undefined and wrongly
    // flip a public object to private. displayData keeps the last known
    // value, matching what the dialog renders.
    const current = selectSummary(displayData)?.visibility;
    const next = current === "private" ? "public" : "private";
    void setVisibilityMutation({
      variables: { input: { [idKey]: id, visibility: next } },
    }).then(() => onVisibilityChange?.());
  }, [
    setVisibilityMutation,
    idKey,
    id,
    displayData,
    selectSummary,
    onVisibilityChange,
  ]);

  // The raw summary already matches the dialog's normalized shape —
  // both object types expose the same fields (incl. hasEditPermission).
  const summary: ShareDialogSummary | null = useMemo(
    () => selectSummary(displayData) ?? null,
    [selectSummary, displayData],
  );
  const loadedMembers = summary?.members.length ?? 0;
  // Use the filtered counts for pagination decisions so an active
  // search doesn't keep `hasMore` true after the last matching row
  // is already on screen.
  const totalFilteredMembers = summary?.filteredMemberCount ?? 0;
  const hasMoreMembers = loadedMembers < totalFilteredMembers;
  const loadedTeams = summary?.teams.length ?? 0;
  const totalFilteredTeams = summary?.filteredTeamCount ?? 0;
  const hasMoreTeams = loadedTeams < totalFilteredTeams;

  const loadMoreMembers = useCallback(() => {
    if (!id || !hasMoreMembers || fetchingMore) return;
    void fetchMore({
      // Re-request page 0 of teams so the merge result stays stable;
      // mergePage throws the team slice away.
      variables: { ...baseVariables, skip: loadedMembers },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult ? mergePage(prev, fetchMoreResult, "members") : prev,
    });
  }, [fetchMore, hasMoreMembers, loadedMembers, fetchingMore, id, baseVariables, mergePage]);

  const loadMoreTeams = useCallback(() => {
    if (!id || !hasMoreTeams || fetchingMore) return;
    void fetchMore({
      variables: { ...baseVariables, teamSkip: loadedTeams },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult ? mergePage(prev, fetchMoreResult, "teams") : prev,
    });
  }, [fetchMore, hasMoreTeams, loadedTeams, fetchingMore, id, baseVariables, mergePage]);

  return {
    summary,
    loading,
    hasMoreMembers,
    loadMoreMembers,
    hasMoreTeams,
    loadMoreTeams,
    setMemberShare,
    setTeamShare,
    removeAllMembers,
    removeAllTeams,
    setAllMembers,
    setAllTeams,
    togglePrivate,
  };
}
